/**
 * Agent Prompt Prefix Service
 *
 * Generic service for loading and managing prompt prefix configuration for all agents.
 * Provides response enhancement prompts (regenerate / crisp / detailed) that can be
 * prepended to user questions when using the 3-dots menu actions.
 *
 * Features:
 * - Centralized prompt prefix loading for all agents
 * - Language-specific prompt support (English, Japanese, Chinese)
 * - Cached configuration (loaded once per agent)
 * - Graceful error handling with fallback to empty prompts
 *
 * Usage:
 *   1. Call initialize(agentId) when agent becomes available
 *   2. Use getEnhancementPrompts(agentId) to retrieve cached prompts
 */

import { Injectable, inject } from '@angular/core';
import { LoggerService } from '../../../../services/logger-services/logger.service';
import { ConfigurationService } from '../../../../services/configuration/configuration.service';

@Injectable({
  providedIn: 'root'
})
export class AgentPromptPrefixService {
  private readonly logger = inject(LoggerService);
  private readonly configurationService = inject(ConfigurationService);

  /** Cached enhancement prompts per agent ID */
  private agentEnhancementPrompts: Map<string, { [key: string]: string }> = new Map();
  
  /** Track which agents have loaded their prompt config */
  private promptPrefixConfigLoaded: Set<string> = new Set();

  // ---------------------------------------------------------------------------
  // PUBLIC INITIALIZATION
  // ---------------------------------------------------------------------------

  /**
   * Pre-fetches prompt prefix configuration for a specific agent.
   * Should be called at application startup when it is known the user has access
   * to an agent (e.g. from AgentWorkspaceComponent.setAvailableAgents).
   *
   * Safe to call multiple times — the underlying API call is cached per agent.
   *
   * @param agentId - The agent ID (e.g., 'via_agent', 'regulations_agent', 'mcc_agent')
   * @param language - Language code ('en' | 'jp' | 'zhCN'). Defaults to 'en'.
   */
  public initialize(agentId: string, language: string = 'en'): void {
    if (!agentId) {
      this.logger.logError('[AgentPromptPrefix] Cannot initialize without agentId', 'initialize');
      return;
    }
    this.fetchPromptPrefixConfig(agentId, language);
  }

  // ---------------------------------------------------------------------------
  // PROMPT PREFIX CONFIG (from app-config API)
  // ---------------------------------------------------------------------------

  /**
   * Fetches response enhancement prompts (regenerate / crisp / detailed) for a given language
   *
   * Payload sent to `/v2/api/app-config`:
   *   { application_name, environment, config_profile_id: 'chatbot_prompt_prefix' }
   *
   * Expected response shape:
   *   body.configuration.regenerate.<languageKey>
   *   body.configuration.crisp.<languageKey>
   *   body.configuration.detailed.<languageKey>
   *
   * Safe to call multiple times — result is cached after the first successful call per agent.
   *
   * @param agentId - The agent ID (e.g., 'via_agent', 'regulations_agent')
   * @param language - Language code ('en' | 'jp' | 'zhCN'). Defaults to 'en'.
   */
  private fetchPromptPrefixConfig(agentId: string, language: string = 'en'): void {
    if (this.promptPrefixConfigLoaded.has(agentId)) {
      return;
    }

    const langMap: { [key: string]: string } = {
      'en': 'english',
      'jp': 'japanese',
      'zhCN': 'chinese'
    };
    const languageKey = langMap[language] || 'english';

    this.configurationService.getFeatureConfig('chatbot_prompt_prefix').subscribe({
      next: (responseData: any) => {
        let prompts: { [key: string]: string };

        if (responseData && responseData.body && responseData.body.configuration) {
          const configuration = responseData.body.configuration;

          // Extract prompts for the specified language
          prompts = {
            'regenerate': configuration.regenerate?.[languageKey] || '',
            'crisp': configuration.crisp?.[languageKey] || '',
            'detailed': configuration.detailed?.[languageKey] || ''
          };
        } else {
          prompts = {
            'regenerate': '',
            'crisp': '',
            'detailed': ''
          };
        }

        // Cache the prompts for this agent
        this.agentEnhancementPrompts.set(agentId, prompts);
        this.promptPrefixConfigLoaded.add(agentId);

        this.logger.logInfo(
          `[AgentPromptPrefix] Enhancement prompts loaded for ${agentId} (lang=${languageKey}): ` +
          `regenerate=${!!prompts['regenerate']}, ` +
          `crisp=${!!prompts['crisp']}, ` +
          `detailed=${!!prompts['detailed']}`,
          'fetchPromptPrefixConfig'
        );
      },
      error: (err: any) => {
        this.logger.logError(
          `[AgentPromptPrefix] fetchPromptPrefixConfig error for ${agentId}: ${err?.message || err}`,
          'fetchPromptPrefixConfig'
        );
        // Store empty prompts on error so streaming is not blocked
        const emptyPrompts = {
          'regenerate': '',
          'crisp': '',
          'detailed': ''
        };
        this.agentEnhancementPrompts.set(agentId, emptyPrompts);
        this.promptPrefixConfigLoaded.add(agentId);
      }
    });
  }

  /**
   * Returns the cached enhancement prompts for a specific agent.
   *
   * @param agentId - The agent ID (e.g., 'via_agent', 'regulations_agent')
   * @returns Object with keys 'regenerate', 'crisp', 'detailed' or empty object if not loaded
   */
  public getEnhancementPrompts(agentId: string): { [key: string]: string } {
    return this.agentEnhancementPrompts.get(agentId) || {
      'regenerate': '',
      'crisp': '',
      'detailed': ''
    };
  }

  /**
   * Check if prompts have been loaded for a specific agent.
   *
   * @param agentId - The agent ID to check
   * @returns true if prompts are loaded, false otherwise
   */
  public isLoaded(agentId: string): boolean {
    return this.promptPrefixConfigLoaded.has(agentId);
  }

  /**
   * Clear cached prompts for a specific agent (useful for testing or re-initialization).
   *
   * @param agentId - The agent ID to clear, or undefined to clear all
   */
  public clearCache(agentId?: string): void {
    if (agentId) {
      this.agentEnhancementPrompts.delete(agentId);
      this.promptPrefixConfigLoaded.delete(agentId);
      this.logger.logInfo(`[AgentPromptPrefix] Cleared cache for ${agentId}`, 'clearCache');
    } else {
      this.agentEnhancementPrompts.clear();
      this.promptPrefixConfigLoaded.clear();
      this.logger.logInfo('[AgentPromptPrefix] Cleared all cached prompts', 'clearCache');
    }
  }
}
